import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ConflictError } from '../api/client';
import type { AuditRow, DailyRecordsResponse, DailyRecordView } from '../api/types';
import { useApp } from '../state/AppContext';
import {
  ConflictBanner, EmployeeName, EmptyRow, ErrorBanner, ExportMenu, FlagPill, GraceBanner,
  HistoryPanel, LocationSelect, LockedBanner, Points, SortableHeader, downloadCsv, formatDate, formatLongDate,
} from '../components/ui';

type StatusFilter = 'all' | 'pending' | 'nopoints' | 'approved' | 'rejected';
type SortColumn = 'employee' | 'workDate' | 'flag' | 'points' | 'status';

// A pending row always carries points now — zero-point days settle themselves
// and live on the No points tab — so "Action pending" says what the tab is:
// the deductions waiting on a person, and nothing else.
const STATUS_LABEL: Record<string, string> = {
  pending: 'Action pending',
  approved: 'Approved',
  'auto-approved': 'Auto-approved',
  rejected: 'Rejected',
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'var(--orange)',
  approved: 'var(--green)',
  'auto-approved': 'var(--green)',
  rejected: 'var(--red)',
};

export function DailySummary() {
  const { period, scopedDepartments, scopedFacilities, can, locationsFor } = useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [from, setFrom] = useState(period.startDate);
  const [to, setTo] = useState(period.endDate);
  const [location, setLocation] = useState('all');
  const [department, setDepartment] = useState('all');
  const [employeeId, setEmployeeId] = useState('all');
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [sort, setSort] = useState<{ column: SortColumn; dir: 'asc' | 'desc' }>({ column: 'workDate', dir: 'desc' });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [comments, setComments] = useState<Record<number, string>>({});
  const [bulkComment, setBulkComment] = useState('');
  const [historyOpen, setHistoryOpen] = useState<number | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rebuilt, setRebuilt] = useState<string | null>(null);

  const canApprove = can('dailySummary', 'approve');
  const canRebuild = can('dataImport', 'edit');

  const query = useQuery({
    queryKey: ['daily', from, to, location, department, employeeId, status],
    queryFn: () =>
      api.get<DailyRecordsResponse>('/api/daily-records', { from, to, location, department, employeeId, status }),
  });

  const reload = () => {
    setConflict(null);
    setSelected(new Set());
    queryClient.invalidateQueries({ queryKey: ['daily'] });
    queryClient.invalidateQueries({ queryKey: ['scorecard'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const review = useMutation({
    mutationFn: ({ row, action }: { row: DailyRecordView; action: 'approve' | 'reject' | 'reopen' }) =>
      api.post(`/api/daily-records/${row.dailyRecordId}/${action}`, {
        rowVersionNo: row.rowVersionNo,
        comment: comments[row.dailyRecordId] ?? '',
      }),
    onSuccess: reload,
    onError: (e) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  const bulkReview = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      api.post<{ applied: number[]; conflicts: number[] }>('/api/daily-records/bulk', {
        ids: [...selected],
        action,
        comment: bulkComment,
      }),
    onSuccess: (result) => {
      setBulkComment('');
      if (result.conflicts.length > 0) {
        setConflict(
          `${result.applied.length} applied, ${result.conflicts.length} skipped because they changed or belong to a locked period.`,
        );
      }
      reload();
    },
    onError: (e) => setError(e.message),
  });

  // Punches and schedules are re-read on every import commit. This runs the same
  // pass on demand, for the changes an import does not carry: a schedule added, a
  // pay code classified, the scorecard scope narrowed, someone marked inactive.
  const rebuild = useMutation({
    mutationFn: () => api.post<{ inserted: number; updated: number; retired: number }>('/api/daily-records/generate', {}),
    onSuccess: (r) => {
      setRebuilt(`${r.inserted} new, ${r.updated} refreshed, ${r.retired} retired`);
      reload();
      setTimeout(() => setRebuilt(null), 8000);
    },
    onError: (e) => setError(e.message),
  });

  const rows = useMemo(() => {
    const list = [...(query.data?.rows ?? [])];
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.column) {
        case 'employee': return a.employee.name.localeCompare(b.employee.name) * dir;
        case 'flag': return a.flag.localeCompare(b.flag) * dir;
        case 'points': return (a.points - b.points) * dir;
        case 'status': return a.status.localeCompare(b.status) * dir;
        default: return a.workDate.localeCompare(b.workDate) * dir;
      }
    });
    return list;
  }, [query.data, sort]);

  const employeeOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of query.data?.rows ?? []) seen.set(r.employee.employeeId, r.employee.name);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [query.data]);

  const pendingIds = rows.filter((r) => r.status === 'pending' && !r.isLocked).map((r) => r.dailyRecordId);
  const allPendingSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));
  const counts = query.data?.counts ?? { pending: 0, approved: 0, rejected: 0 };

  const toggleSort = (column: string) =>
    setSort((s) => ({ column: column as SortColumn, dir: s.column === column && s.dir === 'desc' ? 'asc' : 'desc' }));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Daily Summary Review</div>
          <div className="page-subtitle">
            Auto-generated from HR punch data · {formatLongDate(from)} – {formatLongDate(to)} ·{' '}
            {rows.length} records in range — review and resolve flags
          </div>
        </div>
        <ExportMenu
          onExport={() =>
            downloadCsv(
              `daily-summary-${from}-to-${to}.csv`,
              ['Employee ID', 'Employee', 'Date', 'Flag', 'Points', 'Status', 'Scheduled', 'Clocked', 'Minutes late'],
              rows.map((r) => [
                r.employee.employeeId,
                r.employee.resolved ? r.employee.name : `Employee ${r.employee.employeeId} (not in master)`,
                r.workDate, r.flag, r.points, STATUS_LABEL[r.status],
                [r.scheduledStart, r.scheduledEnd].filter(Boolean).join(' – '),
                [r.clockIn, r.clockOut].filter(Boolean).join(' – '),
                r.minutesLate ?? '',
              ]),
            )
          }
        />
      </div>

      {canRebuild && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button className="btn btn--sm" disabled={rebuild.isPending} onClick={() => rebuild.mutate()}>
            {rebuild.isPending ? 'Rebuilding…' : 'Rebuild daily records'}
          </button>
          <span className="meta">
            {rebuilt ?? 'Re-reads punches and schedules — after a schedule is added, a pay code is classified, '
              + 'or someone is marked inactive. Days you approved or rejected keep their decision.'}
          </span>
        </div>
      )}

      <div className="stat-grid">
        <div className="stat">
          <div className="stat__label">ACTION PENDING</div>
          <div className="stat__value" style={{ color: 'var(--orange)' }}>{counts.pending}</div>
          <div className="stat__note">deductions waiting on approve or reject</div>
        </div>
        <div className="stat">
          <div className="stat__label">APPROVED</div>
          <div className="stat__value" style={{ color: 'var(--green)' }}>{counts.approved}</div>
          <div className="stat__note">includes auto-approved</div>
        </div>
        <div className="stat">
          <div className="stat__label">REJECTED</div>
          <div className="stat__value" style={{ color: 'var(--red)' }}>{counts.rejected}</div>
          <div className="stat__note">not scored</div>
        </div>
      </div>

      <div className="filter-row">
        <select className="control" style={{ maxWidth: 220 }} value={department} onChange={(e) => setDepartment(e.target.value)} aria-label="Department">
          <option value="all">All departments</option>
          <option value="__none__">— No department set —</option>
          {scopedDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <LocationSelect facilities={scopedFacilities} allowed={locationsFor('dailySummary', 'view')} value={location} onChange={setLocation} />

        <select className="control" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} aria-label="Employee">
          <option value="all">All employees</option>
          {employeeOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="date" className="control" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
          <span className="meta">to</span>
          <input type="date" className="control" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
        </div>

        <div className="segmented" style={{ marginLeft: 'auto' }}>
          {(['all', 'pending', 'nopoints', 'approved', 'rejected'] as StatusFilter[]).map((tab) => (
            <button
              key={tab}
              className={`segmented__item${status === tab ? ' segmented__item--active' : ''}`}
              onClick={() => setStatus(tab)}
            >
              {tab === 'all' ? 'All' : tab === 'nopoints' ? 'No points' : STATUS_LABEL[tab]}
            </button>
          ))}
        </div>
      </div>

      <div className="meta" style={{ marginBottom: 8 }}>
        {rows.length} records in the selected date range · scores reflect the scoring period set in Settings (
        {period.name}), so widen the dates to see everything behind a score
      </div>

      {period.status === 'locked' && <LockedBanner periodName={period.name} />}
      {period.status === 'grace' && <GraceBanner periodName={period.name} days={period.gracePeriodDays} />}
      {conflict && <ConflictBanner message={conflict} onReload={reload} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {canApprove && selected.size > 0 && (
        <div className="banner banner--info">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <strong>{selected.size} selected</strong>
            <input
              className="control"
              style={{ flex: 1 }}
              placeholder="Add a comment for all selected (optional)"
              value={bulkComment}
              onChange={(e) => setBulkComment(e.target.value)}
            />
            <button className="btn btn--approve" disabled={bulkReview.isPending} onClick={() => bulkReview.mutate('approve')}>
              Approve selected
            </button>
            <button className="btn btn--reject" disabled={bulkReview.isPending} onClick={() => bulkReview.mutate('reject')}>
              Reject selected
            </button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 20 }}>
                {canApprove && pendingIds.length > 0 && (
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    onChange={() => setSelected(allPendingSelected ? new Set() : new Set(pendingIds))}
                    aria-label="Select all pending"
                  />
                )}
              </th>
              <th className="num">ID</th>
              <SortableHeader label="EMPLOYEE" column="employee" sort={sort} onSort={toggleSort} />
              <SortableHeader label="DATE" column="workDate" sort={sort} onSort={toggleSort} />
              <SortableHeader label="FLAG" column="flag" sort={sort} onSort={toggleSort} />
              <SortableHeader label="POINTS" column="points" sort={sort} onSort={toggleSort} className="num" />
              <SortableHeader label="STATUS" column="status" sort={sort} onSort={toggleSort} />
              <th className="num" style={{ minWidth: 250 }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <EmptyRow colSpan={8}>Loading records…</EmptyRow>}
            {!query.isLoading && rows.length === 0 && (
              <EmptyRow colSpan={8}>
                No records match these filters. Widen the date range, or run an HR import if punch data is missing.
              </EmptyRow>
            )}

            {rows.map((r) => {
              const needsAction = r.status === 'pending' && canApprove && !r.isLocked;
              // A decision made in error should be reversible while the period
              // is still open. Auto-approved rows are excluded: nobody decided
              // those, so there is nothing to undo — regeneration owns them.
              const canUndo = canApprove && !r.isLocked
                && (r.status === 'approved' || r.status === 'rejected');
              return (
                <Fragment key={r.dailyRecordId}>
                  <tr className={r.isLocked ? 'locked' : undefined}>
                    <td>
                      {needsAction && (
                        <input
                          type="checkbox"
                          checked={selected.has(r.dailyRecordId)}
                          onChange={() =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              next.has(r.dailyRecordId) ? next.delete(r.dailyRecordId) : next.add(r.dailyRecordId);
                              return next;
                            })
                          }
                          aria-label={`Select ${r.employee.name}`}
                        />
                      )}
                    </td>
                    <td className="num">{r.employee.employeeId}</td>
                    <td
                      className="strong"
                      style={{ cursor: r.employee.resolved ? 'pointer' : 'default' }}
                      onClick={() => r.employee.resolved && navigate(`/employee/${r.employee.employeeId}`)}
                    >
                      <EmployeeName employee={r.employee} subtext={r.employee.locationCode} />
                    </td>
                    <td>{formatDate(r.workDate)}</td>
                    <td>
                      <FlagPill flag={r.flag} category={r.flagCategory} tooltip={tooltipFor(r)} />
                    </td>
                    <td className="num"><Points value={r.points} /></td>
                    <td>
                      <span style={{ fontSize: 12, fontWeight: 600, color: STATUS_COLOR[r.status] }}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="num">
                      {needsAction ? (
                        <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <input
                            className="control"
                            style={{ width: 110, padding: '6px 8px', fontSize: 11.5 }}
                            placeholder="Comment (optional)"
                            value={comments[r.dailyRecordId] ?? ''}
                            onChange={(e) => setComments((c) => ({ ...c, [r.dailyRecordId]: e.target.value }))}
                          />
                          <button className="btn btn--approve" disabled={review.isPending}
                            onClick={() => review.mutate({ row: r, action: 'approve' })}>
                            Approve
                          </button>
                          <button className="btn btn--reject" disabled={review.isPending}
                            onClick={() => review.mutate({ row: r, action: 'reject' })}>
                            Reject
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {canUndo && (
                            <>
                              <button
                                className="btn btn--sm"
                                disabled={review.isPending}
                                title="Send this back to Action pending"
                                onClick={() => review.mutate({ row: r, action: 'reopen' })}
                              >
                                Undo
                              </button>
                              <button
                                className={r.status === 'approved' ? 'btn btn--reject' : 'btn btn--approve'}
                                disabled={review.isPending}
                                onClick={() => review.mutate({
                                  row: r, action: r.status === 'approved' ? 'reject' : 'approve',
                                })}
                              >
                                {r.status === 'approved' ? 'Reject' : 'Approve'}
                              </button>
                            </>
                          )}
                          <button
                            className="btn btn--sm"
                            style={{ border: 'none', background: 'transparent', color: 'var(--muted)', textDecoration: 'underline' }}
                            onClick={() => setHistoryOpen(historyOpen === r.dailyRecordId ? null : r.dailyRecordId)}
                          >
                            history
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>

                  {historyOpen === r.dailyRecordId && (
                    <tr className="history-row">
                      <td colSpan={8}>
                        <RecordHistory id={r.dailyRecordId} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {!canApprove && (
        <div className="meta" style={{ marginTop: 10 }}>
          Your role can view these records but not approve or reject them.
        </div>
      )}
    </div>
  );
}

/** Same expandable trail used for Points Key, incidents and schedules. */
function RecordHistory({ id }: { id: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['daily-history', id],
    queryFn: () => api.get<AuditRow[]>(`/api/daily-records/${id}/history`),
  });
  return <HistoryPanel rows={data} loading={isLoading} />;
}

function tooltipFor(r: DailyRecordView): string {
  const parts = [
    `Scheduled ${r.scheduledStart ?? '—'}–${r.scheduledEnd ?? '—'}`,
    `Clocked ${r.clockIn ?? '—'}–${r.clockOut ?? '—'}`,
  ];
  if (r.minutesLate) parts.push(`${r.minutesLate} min late`);
  if (r.minutesEarly) parts.push(`${r.minutesEarly} min early departure`);
  if (r.payCode) parts.push(`HR pay code: ${r.payCode}`);
  return parts.join('  ·  ');
}
